// Copyright (c) Cosmo Tech.
// Licensed under the MIT license.
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { Checkbox, MenuItem, Select, TextField, useFormControl } from '@mui/material';
import { FadingTooltip } from '@cosmotech/ui';
import { WizardUtils } from '../../../utils/WizardUtils';
import { AutocompleteField } from './AutocompleteField';
import MultiInputField from './MultiInputField';
import NestedSelect from './NestedSelect';

export const TableCellComponent = ({
  col,
  currentRow = {},
  handleChange,
  isCellDisabled,
  isCellValid,
  selectedAssetClassAttrsList,
  referentielConsequences = [],
}) => {
  const { t } = useTranslation();
  const { disabled } = useFormControl();
  const inputType = useMemo(() => {
    return typeof col.inputType === 'function' ? col.inputType(currentRow) : col.inputType;
  }, [col, currentRow]);

  useEffect(() => {
    if (typeof col.disabled === 'function' && disabled) {
      if (col.clearIfDisabled) handleChange(col, '');
      else if (col.defaultValue) handleChange(col, col.defaultValue);
    }
  }, [col, disabled, handleChange]);

  const handleAttributeChange = (e) => {
    handleChange(col, e.target.value);
  };
  const getHelperText = () => {
    if (!isCellValid || !isCellValid(col)) return null;
    const val = currentRow?.[col.field];
    const isEmpty = val == null || (typeof val === 'string' && val.trim() === '');
    if (col.required && isEmpty) {
      return t('genericcomponent.common.validationErrors.mustNotBeEmpty', 'Must not be empty');
    }
    if (col.field === 'Identifiant' || col.field === 'IdentifiantAction') {
      return t('genericcomponent.common.validationErrors.identifierAlreadyExists', 'The identifier already exists');
    }
    return t('genericcomponent.common.validationErrors.invalidValue', 'Invalid value');
  };

  return inputType === 'number' ? (
    <TextField
      id={col.field}
      value={currentRow[col.field] ?? ''}
      type="number"
      InputProps={{
        inputProps: {
          step: col.step ?? 'any',
          min: col.min,
          max: col.max,
        },
      }}
      onChange={(e) => {
        const value = e.target.value;
        if (col.step === 1) {
          const parsedValue = parseInt(value);
          const finalValue = isNaN(parsedValue) ? '' : parsedValue;
          handleChange(col, finalValue);
        } else {
          handleChange(col, value);
        }
      }}
      fullWidth
      disabled={isCellDisabled(col)}
    />
  ) : inputType === 'select' ? (
    col.field === 'ValeurReference' ? (
      <MultiInputField
        value={currentRow[col.field]}
        onChange={(value) => handleChange(col, value)}
        options={col.options}
        selectedAssetClassAttrsList={selectedAssetClassAttrsList}
        attributDeclencheur={currentRow.AttributDeclencheur}
        disabled={isCellDisabled(col)}
        label={col.label}
      />
    ) : (
      <Select
        value={currentRow[col.field]}
        displayEmpty={true}
        data-testid={col.label}
        onChange={(e) => handleChange(col, e.target.value)}
        fullWidth
        disabled={isCellDisabled(col)}
      >
        {col.options?.map((option) => (
          <MenuItem
            key={option.value ?? option}
            data-cy={option.value || option.label || option}
            value={option.value ?? option}
          >
            {option.label ?? option}
          </MenuItem>
        ))}
      </Select>
    )
  ) : inputType === 'nestedSelect' ? (
    <NestedSelect
      value={currentRow[col.field]}
      onChange={handleAttributeChange}
      options={col.options}
      disabled={isCellDisabled(col)}
      fullWidth={true}
      testId={col.label}
      selectedAssetClassAttrsList={selectedAssetClassAttrsList}
      referentielConsequences={referentielConsequences}
    />
  ) : inputType === 'multiSelect' ? (
    <Select
      value={currentRow[col.field] ? currentRow[col.field].split('|') : []}
      renderValue={(selected) => {
        return (
          <FadingTooltip useSpan={true} title={selected?.join('|')}>
            {selected?.join('|')}
          </FadingTooltip>
        );
      }}
      fullWidth
      multiple
      data-testid={col.label}
      onChange={(e) => handleChange(col, e.target.value?.join('|'))}
      disabled={isCellDisabled(col)}
      sx={{ maxWidth: WizardUtils.getMaxCharWidth(col.length) }}
    >
      {[
        ...(col.options || []),
        ...(currentRow[col.field]
          ? currentRow[col.field]
              .split('|')
              .filter((value) => !(col.options || []).some((opt) => (opt?.value ?? opt) === value))
              .map((value) => ({ value, label: value }))
          : []), // .map((value) => ({ value, label: `${value} (supprimée)` }))
      ].map((option) => (
        <MenuItem data-cy={option?.value ?? option} key={option?.value ?? option} value={option?.value ?? option}>
          <Checkbox checked={!!currentRow[col.field]?.split('|')?.includes(option?.value ?? option)} />
          {option.label ?? option}
        </MenuItem>
      ))}
    </Select>
  ) : inputType === 'autocomplete' ? (
    <AutocompleteField
      value={currentRow[col.field]}
      onChange={(newValue) => handleChange(col, newValue)}
      col={col}
      currentRow={currentRow}
      disabled={isCellDisabled(col)}
      id={col.label}
    />
  ) : inputType === 'buttonWithMultiSelect' ? (
    <Select
      value={currentRow[col.field] ? currentRow[col.field].split('|') : []}
      renderValue={(selected) => {
        return (
          <FadingTooltip useSpan={true} title={selected?.join('|')}>
            {selected?.join('|')}
          </FadingTooltip>
        );
      }}
      fullWidth
      multiple
      data-testid={col.label}
      onChange={(e) => handleChange(col, e.target.value?.join('|'))}
      disabled={isCellDisabled(col)}
      sx={{ maxWidth: WizardUtils.getMaxCharWidth(col.length) }}
    >
      {[
        ...(col.options || []),
        ...(currentRow[col.field]
          ? currentRow[col.field]
              .split('|')
              .filter((value) => !(col.options || []).some((opt) => (opt?.value ?? opt) === value))
              .map((value) => ({ value, label: value }))
          : []), // .map((value) => ({ value, label: `${value} (supprimée)` }))
      ].map((option) => (
        <MenuItem data-cy={option?.value ?? option} key={option?.value ?? option} value={option?.value ?? option}>
          <Checkbox checked={!!currentRow[col.field]?.split('|')?.includes(option?.value ?? option)} />
          {option.label ?? option}
        </MenuItem>
      ))}
    </Select>
  ) : (
    <TextField
      id={col.field}
      value={currentRow[col.field]}
      onChange={(e) => handleChange(col, e.target.value)}
      fullWidth
      disabled={isCellDisabled(col)}
      error={isCellValid(col)}
      helperText={getHelperText()}
    />
  );
};

TableCellComponent.propTypes = {
  col: PropTypes.object.isRequired,
  handleChange: PropTypes.func.isRequired,
  currentRow: PropTypes.object,
  isCellDisabled: PropTypes.func,
  isCellValid: PropTypes.func,
  selectedAssetClassAttrsList: PropTypes.array,
  referentielConsequences: PropTypes.array,
};
